import { PortionCount } from '@mindfulchefuk/config/portionCountConfig'
import {
  Macros,
  RecipeWithQuantity,
  SpicinessLevel,
} from '@mindfulchefuk/features/Recipes/interfaces'
import {
  TRecipeAvailabilityAllMacros,
  TRecipeAvailability,
  TExpandedAvailabilityRecipe,
  TSummaryAvailabilityRecipe,
} from '@mindfulchefuk/types/recipeAvailabilities'

export const transformExpandedAvailabilityToRecipeWithQuantity = ({
  recipeAvailability,
  recipesInBasket,
}: {
  recipeAvailability: TRecipeAvailability<TExpandedAvailabilityRecipe>
  recipesInBasket: { [recipeId: string]: number }
}): RecipeWithQuantity => {
  const getMacros = (macrosPerPerson: TRecipeAvailabilityAllMacros): Macros => {
    if (macrosPerPerson['2Portion'].calories) {
      return macrosPerPerson['2Portion'] as Macros
    }

    if (macrosPerPerson['4Portion'].calories) {
      return macrosPerPerson['4Portion'] as Macros
    }

    return macrosPerPerson['1Portion'] as Macros
  }

  const stringRecipeId = recipeAvailability.recipe.id.toString()

  const quantity = recipesInBasket[stringRecipeId] || 0

  return {
    allergens: recipeAvailability.recipe.allergens,
    beforeYouStart: recipeAvailability.recipe.beforeYouStart,
    availabilityId: recipeAvailability.availabilityId,
    averageRating: null,
    badges: recipeAvailability.recipe.badges,
    carbonRating: recipeAvailability.recipe.carbonRating,
    claims: recipeAvailability.recipe.tags,
    cookingInstructions: recipeAvailability.recipe.cookingInstructions.map(
      (instruction) => ({
        title: instruction.title,
        description: instruction.description,
      })
    ),
    cookingTime: recipeAvailability.recipe.cookingTime,
    cuisine: recipeAvailability.recipe.cuisine,
    description: recipeAvailability.recipe.description,
    feedback: recipeAvailability.recipe.feedback,
    foodGroup: recipeAvailability.recipe.foodGroup,
    id: stringRecipeId,
    imageUrl: recipeAvailability.recipe.image,
    ingredients: null,
    keyIngredient: recipeAvailability.recipe.keyIngredientDescription,
    macros: {
      macrosPerPerson: getMacros(recipeAvailability.recipe.macrosPerPerson),
      macrosPer100g: getMacros(recipeAvailability.recipe.macrosPer100g),
    },
    prepTime: recipeAvailability.recipe.prepTime,
    portions: recipeAvailability.portions as PortionCount,
    possibleAllergens: recipeAvailability.recipe.possibleAllergens,
    price: Number(recipeAvailability.price),
    pricePerPerson: Number(recipeAvailability.pricePerPerson),
    quantity,
    quantifiedIngredients: recipeAvailability.recipe.recipeComponents.map(
      (component) => ({
        id: component.id.toString(),
        name: component.title,
        componentId: component.id.toString(),
        displayTitle: component.title,
        allergens: component.allergens,
        possibleAllergens: component.possibleAllergens,
      })
    ),
    sortOrder: recipeAvailability.sortOrder,
    spicinessLevel: recipeAvailability.recipe.spicinessLevel as SpicinessLevel,
    title: recipeAvailability.recipe.title,
  }
}

export const transformSummaryAvailabilityToRecipeWithQuantity = ({
  recipeAvailability,
  recipesInBasket,
}: {
  recipeAvailability: TRecipeAvailability<TSummaryAvailabilityRecipe>
  recipesInBasket: { [recipeId: string]: number }
}): RecipeWithQuantity => {
  const getMacros = (macrosPerPerson: TRecipeAvailabilityAllMacros): Macros => {
    if (macrosPerPerson['2Portion'].calories) {
      return macrosPerPerson['2Portion'] as Macros
    }

    if (macrosPerPerson['4Portion'].calories) {
      return macrosPerPerson['4Portion'] as Macros
    }

    return macrosPerPerson['1Portion'] as Macros
  }

  const stringRecipeId = recipeAvailability.recipe.id.toString()
  const quantity = recipesInBasket[stringRecipeId] || 0

  return {
    availabilityId: recipeAvailability.availabilityId,
    averageRating: null,
    badges: recipeAvailability.recipe.badges,
    claims: recipeAvailability.recipe.tags,
    carbonRating: undefined,
    cookingTime: recipeAvailability.recipe.cookingTime,
    cuisine: recipeAvailability.recipe.cuisine,
    description: recipeAvailability.recipe.description,
    feedback: recipeAvailability.recipe.feedback,
    foodGroup: recipeAvailability.recipe.foodGroup,
    id: stringRecipeId,
    imageUrl: recipeAvailability.recipe.image,
    ingredients: null,
    keyIngredient: recipeAvailability.recipe.keyIngredientDescription,
    macros: {
      macrosPerPerson: getMacros(recipeAvailability.recipe.macrosPerPerson),
      macrosPer100g: getMacros(recipeAvailability.recipe.macrosPer100g),
    },
    prepTime: 0,
    portions: recipeAvailability.portions as PortionCount,
    possibleAllergens: recipeAvailability.recipe.possibleAllergens,
    price: Number(recipeAvailability.price),
    pricePerPerson: Number(recipeAvailability.pricePerPerson),
    quantity,
    quantifiedIngredients: [],
    sortOrder: recipeAvailability.sortOrder,
    spicinessLevel: recipeAvailability.recipe.spicinessLevel as SpicinessLevel,
    title: recipeAvailability.recipe.title,
  }
}
